import React, { useMemo, useState } from "react";
import { Button, Input, Select, Tags } from "../../ui";
import { SummaryCard } from "./DashboardBase";
import { exportDashboardRowsToExcel } from "../../../utils/ludDashboardExcel";

const ALL_CLIENT_ACTION_TYPES = [
  "اشتراك",
  "كشف حساب",
  "إضافة فرع",
  "فاتورة ضريبية",
  "عقد",
];

function normalizeAllClientsRequest(request, index, restaurants) {
  const restaurant =
    restaurants.find(
      (item) =>
        item.id === request.restaurantId ||
        item.nameAr === request.target,
    ) || restaurants[index % Math.max(restaurants.length, 1)] || { id: request.restaurantId || "", nameAr: request.target || "", nameEn: request.target || "" };

  return {
    ...request,
    restaurantId: restaurant.id,
    restaurantNameAr: restaurant.nameAr,
    restaurantNameEn: restaurant.nameEn,
  };
}

function actionStatusMeta(status, lang) {
  const map = {
    pending: {
      ar: "جاري العمل",
      en: "In Progress",
      variant: "warning",
    },
    processing: {
      ar: "قيد المعالجة",
      en: "Processing",
      variant: "warning",
    },
    awaitingSignature: {
      ar: "بانتظار توقيع العقد",
      en: "Waiting for Signature",
      variant: "warning",
    },
    awaitingContractReview: {
      ar: "بانتظار مراجعة الإدارة",
      en: "Waiting for Admin Review",
      variant: "warning",
    },
    approved: {
      ar: "مكتمل",
      en: "Completed",
      variant: "success",
    },
    completed: {
      ar: "مكتمل",
      en: "Completed",
      variant: "success",
    },
    rejected: {
      ar: "مرفوض",
      en: "Rejected",
      variant: "danger",
    },
  };

  const meta = map[status] || map.pending;

  return {
    label: lang === "ar" ? meta.ar : meta.en,
    variant: meta.variant,
  };
}

function exportAllClientRequests(rows, lang) {
  const headers = lang === "ar"
    ? ["رقم الطلب", "العميل", "معرف العميل", "النوع", "التفاصيل", "التاريخ", "الحالة"]
    : ["Request ID", "Client", "Client ID", "Type", "Details", "Date", "Status"];
  const tableRows = rows.map((request) => {
    const status = actionStatusMeta(request.status, lang);
    return [
      request.id,
      lang === "ar" ? request.restaurantNameAr : request.restaurantNameEn,
      request.restaurantId,
      request.type,
      request.details,
      request.createdAt || "",
      status.label,
    ];
  });
  exportDashboardRowsToExcel({
    filename: "lud-actions-all-clients.xlsx",
    sheetName: lang === "ar" ? "الإجراءات" : "Actions",
    rows: [headers, ...tableRows],
  });
}

export default function AllClientsActionsView({
  lang,
  dashboard,
  onReview,
}) {
  const RESTAURANTS = useMemo(() => dashboard.stores || [], [dashboard.stores]);
  const [restaurantId, setRestaurantId] =
    useState("");
  const [restaurantSearch, setRestaurantSearch] =
    useState("");
  const [type, setType] = useState("");

  const allRequests = useMemo(() => {
    const sourceRequests = dashboard.allStoreData.length ? dashboard.allStoreData.flatMap((item) => item.requests.map((request) => ({ ...request, restaurantId: item.store.id }))) : dashboard.requests;
    const real = sourceRequests.map(
      (request, index) =>
        normalizeAllClientsRequest(request, index, RESTAURANTS),
    );
    return real;
  }, [dashboard.requests, dashboard.allStoreData, RESTAURANTS]);

  const visibleRequests = useMemo(() => {
    const query = restaurantSearch
      .trim()
      .toLowerCase();

    return allRequests.filter((request) => {
      if (
        restaurantId &&
        request.restaurantId !== restaurantId
      ) {
        return false;
      }

      if (type && request.type !== type) {
        return false;
      }

      if (!query) return true;

      const haystack = `${request.restaurantNameAr} ${request.restaurantNameEn} ${request.restaurantId} ${request.id} ${request.type} ${request.details}`.toLowerCase();

      return haystack.includes(query);
    });
  }, [
    allRequests,
    restaurantId,
    restaurantSearch,
    type,
  ]);

  const currentRows = visibleRequests.filter(
    (request) =>
      ![
        "approved",
        "completed",
        "rejected",
      ].includes(request.status),
  );
  const completedRows = visibleRequests.filter(
    (request) =>
      [
        "approved",
        "completed",
        "rejected",
      ].includes(request.status),
  );

  const typeCounts = useMemo(() => {
    return ALL_CLIENT_ACTION_TYPES.map(
      (actionType) => ({
        type: actionType,
        count: allRequests.filter(
          (request) => request.type === actionType,
        ).length,
      }),
    );
  }, [allRequests]);

  const mostFrequent = [...typeCounts].sort(
    (first, second) =>
      second.count - first.count,
  )[0];

  const maxTypeCount = Math.max(
    1,
    ...typeCounts.map((item) => item.count),
  );

  const renderRequestRows = (rows) => {
    if (!rows.length) {
      return (
        <p className="lud-empty-state">
          {lang === "ar"
            ? "لا توجد إجراءات في هذا القسم"
            : "No actions in this section"}
        </p>
      );
    }

    return rows.map((request) => {
      const status = actionStatusMeta(
        request.status,
        lang,
      );
      const isReal = dashboard.requests.some(
        (item) => item.id === request.id,
      );

      return (
        <article
          className="lud-all-actions-item"
          key={request.id}
        >
          <div className="lud-all-actions-icon">
            {request.type?.slice(0, 1)}
          </div>

          <div className="lud-all-actions-main">
            <div className="lud-all-actions-top">
              <strong>{request.type}</strong>

              <Tags
                variant="neutral"
                appearance="soft"
                size="sm"
              >
                {lang === "ar"
                  ? request.restaurantNameAr
                  : request.restaurantNameEn}
              </Tags>

              <span>{request.id}</span>
            </div>

            <p>{request.details}</p>
          </div>

          <time>{request.createdAt || "—"}</time>

          <Tags
            variant={status.variant}
            appearance="soft"
            size="sm"
          >
            {status.label}
          </Tags>

          {isReal &&
            dashboard.role === "admin" &&
            request.status === "pending" && (
              <div className="lud-all-actions-review">
                <Button
                  size="sm"
                  variant="success"
                  appearance="soft"
                  onClick={() =>
                    onReview(request.id, "approved")
                  }
                >
                  {lang === "ar"
                    ? "موافقة"
                    : "Approve"}
                </Button>

                <Button
                  size="sm"
                  variant="danger"
                  appearance="filled"
                  onClick={() =>
                    onReview(request.id, "rejected")
                  }
                >
                  {lang === "ar" ? "رفض" : "Reject"}
                </Button>
              </div>
            )}
        </article>
      );
    });
  };

  return (
    <div
      className="lud-dashboard-subpage lud-all-actions-page"
      dir={lang === "ar" ? "rtl" : "ltr"}
    >
      <div className="lud-dashboard-subpage-head">
        <div>
          <h1>
            {lang === "ar"
              ? "صفحة الإجراءات — كل العملاء"
              : "Actions Page — All Clients"}
          </h1>
        </div>

        <Button
          onClick={() =>
            dashboard.setPage("dashboard")
          }
        >
          {lang === "ar"
            ? "رجوع للوحة الرئيسية"
            : "Back"}
        </Button>
      </div>

      <section className="lud-dashboard-card lud-all-actions-client-filter">
        <div className="lud-dashboard-section-title">
          <h2>
            {lang === "ar"
              ? "فلترة حسب المطعم"
              : "Filter by Restaurant"}
          </h2>
        </div>

        <div className="lud-all-actions-client-filter-grid">
          <Select
            value={restaurantId}
            onChange={(event) =>
              setRestaurantId(event.target.value)
            }
            options={[
              {
                value: "",
                label:
                  lang === "ar"
                    ? "كل المطاعم"
                    : "All Restaurants",
              },
              ...RESTAURANTS.map((restaurant) => ({
                value: restaurant.id,
                label:
                  lang === "ar"
                    ? restaurant.nameAr
                    : restaurant.nameEn,
              })),
            ]}
          />

          <Input
            value={restaurantSearch}
            onChange={(event) =>
              setRestaurantSearch(event.target.value)
            }
            placeholder={
              lang === "ar"
                ? "ابحث باسم المطعم أو المعرف"
                : "Search by restaurant name or ID"
            }
          />
          <Button
  variant="danger"
  appearance="soft"
  onClick={() => {
    setRestaurantId("");
    setRestaurantSearch("");
    setType("");
  }}
>
  {lang === "ar" ? "مسح الفلاتر" : "Clear Filters"}
</Button>
        </div>
      </section>

      <section className="lud-dashboard-card lud-all-actions-overview">
        <div className="lud-dashboard-section-title">
          <div>
            <h2>
              {lang === "ar"
                ? "الإجراءات — كل العملاء"
                : "Actions — All Clients"}
            </h2>

            <p className="lud-muted">
              {lang === "ar"
                ? "كل الطلبات والإجراءات من كل المطاعم في مكان واحد — بدل ما تفتح كل مطعم لحاله."
                : "All requests and actions from every restaurant in one place."}
            </p>
          </div>

          <Button
            appearance="soft"
            onClick={() =>
              exportAllClientRequests(
                allRequests,
                lang,
              )
            }
          >
            {lang === "ar"
              ? "تصدير Excel — الإجراءات"
              : "Export Excel — Actions"}
          </Button>
        </div>

        <div className="lud-all-actions-summary">
          <SummaryCard
            label={
              lang === "ar"
                ? "إجمالي الطلبات"
                : "Total Requests"
            }
            value={allRequests.length}
          />

          <SummaryCard
            label={
              lang === "ar" ? "جارية" : "In Progress"
            }
            value={
              allRequests.filter(
                (request) =>
                  ![
                    "approved",
                    "completed",
                    "rejected",
                  ].includes(request.status),
              ).length
            }
            tone="warning"
          />

          <SummaryCard
            label={
              lang === "ar" ? "مكتملة" : "Completed"
            }
            value={
              allRequests.filter((request) =>
                [
                  "approved",
                  "completed",
                  "rejected",
                ].includes(request.status),
              ).length
            }
            tone="success"
          />

          <SummaryCard
            label={`${lang === "ar"
              ? "الأكثر تكرارًا"
              : "Most Frequent"}: ${
              mostFrequent?.type || "—"
            }`}
            value={mostFrequent?.count || 0}
          />
        </div>

        <div className="lud-all-actions-chart-card">
          <h3>
            {lang === "ar"
              ? "توزيع الطلبات حسب النوع"
              : "Requests by Type"}
          </h3>

          <div className="lud-all-actions-chart">
            {typeCounts.map((item) => (
              <div
                className="lud-all-actions-bar-column"
                key={item.type}
              >
                <span>{item.count}</span>

                <div
                  className="lud-all-actions-bar"
                  style={{
                    height: `${Math.max(
                      18,
                      (item.count / maxTypeCount) *
                        100,
                    )}%`,
                  }}
                />

                <strong>{item.type}</strong>
              </div>
            ))}
          </div>
        </div>

        <div className="lud-all-actions-list-filters">
          <Select
            value={type}
            onChange={(event) =>
              setType(event.target.value)
            }
            options={[
              {
                value: "",
                label:
                  lang === "ar"
                    ? "النوع — الكل"
                    : "All Types",
              },
              ...ALL_CLIENT_ACTION_TYPES.map(
                (actionType) => ({
                  value: actionType,
                  label: actionType,
                }),
              ),
            ]}
          />

          <Button
            appearance="soft"
            onClick={() => {
              setType("");
              setRestaurantId("");
              setRestaurantSearch("");
            }}
          >
            {lang === "ar"
              ? "إعادة تعيين"
              : "Reset"}
          </Button>
        </div>

        <details
          className="lud-all-actions-group"
          open
        >
          <summary>
            <span>
              {lang === "ar" ? "جارية" : "In Progress"}
            </span>

            <Tags
              variant="warning"
              appearance="soft"
              size="sm"
            >
              {currentRows.length}
            </Tags>
          </summary>

          <div className="lud-all-actions-list">
            {renderRequestRows(currentRows)}
          </div>
        </details>

        <details className="lud-all-actions-group">
          <summary>
            <span>
              {lang === "ar" ? "مكتملة" : "Completed"}
            </span>

            <Tags
              variant="success"
              appearance="soft"
              size="sm"
            >
              {completedRows.length}
            </Tags>
          </summary>

          <div className="lud-all-actions-list">
            {renderRequestRows(completedRows)}
          </div>
        </details>
      </section>
    </div>
  );
}

